import { useQuery } from '@tanstack/react-query'
import { LakehouseTableStatusEnum } from '@vendia/management-api-types'
import debug from 'debug'
import { useRecoilValue } from 'recoil'
import { Table } from 'src/types/types'

import { useNodeApi } from '../api-provider'
import { selectedRoleState } from '../state'

const logger = debug('app:get-lakehouse-data')

export const useGetLakehouseData = (filterDeletedTables: boolean = true) => {
  const nodeApi = useNodeApi()
  const { uniName, urqlClientNodeName: nodeName } = nodeApi

  const selectedRoleName = useRecoilValue(selectedRoleState).name

  const enabled = Boolean(nodeApi?.urqlClient) && Boolean(uniName) && Boolean(selectedRoleName)

  return useQuery({
    queryKey: ['LakehouseData', selectedRoleName, uniName, nodeName, filterDeletedTables],
    queryFn: async () => {
      const dataPlaneTables = (await fetchVendiaTables(nodeApi?.urqlClient, filterDeletedTables)) as Table[]

      const tables = dataPlaneTables.filter((table) => {
        if (!table.source) {
          return false
        }

        if (filterDeletedTables && table.status === LakehouseTableStatusEnum.Deleted) {
          return false
        }

        return true
      }) as Table[]

      logger('tables', tables)
      return { tables }
    },
    enabled,
    staleTime: 1000 * 300, // 5 minutes
  })
}

const fetchVendiaTables = async (urqlClient: any, filterDeletedTables: boolean = true) => {
  const query = getListTablesQuery(filterDeletedTables)
  const result = await urqlClient?.query(query, {}).toPromise()
  if (result?.error) {
    throw new Error(result.error?.message)
  }
  const tables = result?.data?.listVendia_TableItems?.Vendia_TableItems ?? []
  try {
    const niceTables = tables.map((table: any) => {
      const tableConfig = JSON.parse(table?.tableConfig ?? '{}')
      return { ...table, ...tableConfig } as Table
    })
    return niceTables
  } catch (e) {
    console.error('Error parsing tableConfig', e)
  }
}

const getListTablesQuery = (filterDeletedTables: boolean = true) => {
  const filterClause = filterDeletedTables ? ', filter: { status: { ne: "DELETED" } }' : ''

  return /* GraphQL */ `
    query ListTables {
      listVendia_TableItems(order: { _id: DESC }${filterClause}) {
        Vendia_TableItems {
          _id
          status
          _acl {
            operations
            principal {
              nodes
            }
            path
          }
          columns {
            fieldId
            name
            fieldType
            required
            identifier
            comment
          }
          name
          policies {
            columnPolicies {
              effect
              name
              value
              operator
            }
            rowPolicies {
              effect
              name
              operator
              value
            }
            defaultColumnPolicyEffect
          }
          source {
            definition {
              snowflake {
                tableName
                connectionId
                lastUpdatedColumn
                lastUpdatedColumnFormat
                keyColumns
              }
              postgres {
                tableName
                connectionId
                lastUpdatedColumn
                lastUpdatedColumnFormat
                keyColumns
              }
              mysql {
                tableName
                connectionId
                lastUpdatedColumn
                lastUpdatedColumnFormat
                keyColumns
              }
              cloudera {
                tableName
                connectionId
                lastUpdatedColumn
                lastUpdatedColumnFormat
                keyColumns
              }
              bigquery {
                connectionId
                keyColumns
                lastUpdatedColumn
                lastUpdatedColumnFormat
                tableName
              }
              databricks {
                connectionId
                keyColumns
                lastUpdatedColumn
                lastUpdatedColumnFormat
                tableName
              }
              vendia {
                tableName
                lastUpdatedColumn
                lastUpdatedColumnFormat
                keyColumns
                joinQuery
              }
              upload {
                csvColumns
                fileName
                csvOptions {
                  dateFormat
                  delimiter
                  enclosure
                  errorHandling
                  header
                  timeFormat
                  timestampFormat
                  whitespace
                }
              }
              s3 {
                connectionId
                csvColumns
                fileName
                csvOptions {
                  dateFormat
                  delimiter
                  enclosure
                  errorHandling
                  header
                  timeFormat
                  timestampFormat
                  whitespace
                }
              }
            }
          }
          _owner
        }
        nextToken
      }
    }
  `
}
